<template>
  <aside v-if="selectedCheckoutFlow == 0" class="gs-checkout-steps" aria-label="Checkout setup progress">
    <div class="gs-checkout-steps__intro">
      <p>Checkout setup</p>
      <h3>{{ activeTab ? activeTab.label : 'Configure checkout' }}</h3>
      <span>{{ activeTab ? activeTab.description : 'Complete each step to publish a working checkout.' }}</span>
    </div>

    <ol class="gs-checkout-steps__list">
      <li
        v-for="(tab, index) in tabs"
        :key="tab.value"
        :class="{
          'is-active': index === currentIndex,
          'is-complete': index < currentIndex,
          'has-error': errorTabs.includes(tab.value),
        }"
      >
        <button
          type="button"
          :disabled="isSaving || !canNavigateTo(index)"
          :aria-current="index === currentIndex ? 'step' : null"
          @click="selectTab(tab)"
        >
          <span class="gs-checkout-steps__number" aria-hidden="true">
            <svg v-if="index < currentIndex" viewBox="0 0 16 16">
              <path d="m3.5 8.2 2.8 2.8 6.2-6.2" />
            </svg>
            <template v-else>{{ index + 1 }}</template>
          </span>
          <span class="gs-checkout-steps__label">
            <strong>{{ tab.label }}</strong>
            <small>{{ tab.description }}</small>
          </span>
        </button>
      </li>
    </ol>

    <div class="gs-checkout-steps__actions">
      <p>Step {{ currentIndex + 1 }} of {{ tabs.length }}</p>
      <div>
        <gs-button
          v-if="currentTab !== 'ConfigStep'"
          class="gs-checkout-steps__action"
          size="sm"
          variant="outline"
          :disabled="isSaving"
          @click="moveBackOnWizard"
        >
          {{ $t('frontend_back') }}
        </gs-button>
        <gs-button
          v-if="currentTab !== 'LinksStep'"
          class="gs-checkout-steps__action gs-checkout-steps__action--primary"
          size="sm"
          variant="primary"
          :loading="isSaving"
          @click="moveNextOnWizard"
        >
          Save &amp; continue
        </gs-button>
        <gs-button
          v-else
          class="gs-checkout-steps__action gs-checkout-steps__action--primary"
          size="sm"
          variant="primary"
          :disabled="isSaving"
          @click="closeWizard"
        >
          Done
        </gs-button>
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  data() {
    return {
      furthestVisitedIndex: 0,
    };
  },

  props: {
    selectedCheckoutFlow: {
      type: Number,
      default: 0,
    },
    currentTab: {
      type: String,
      default: 'ConfigStep',
    },
    errorTabs: {
      type: Array,
      default: () => [],
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    moveBackOnWizard: {
      type: Function,
      default: () => {},
    },
    moveNextOnWizard: {
      type: Function,
      default: () => {},
    },
    selectTab: {
      type: Function,
      default: () => {},
    },
    closeWizard: {
      type: Function,
      default: () => {},
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    allowAllTabs: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    currentIndex() {
      const index = this.tabs.findIndex((tab) => tab.value === this.currentTab);
      return index === -1 ? 0 : index;
    },

    activeTab() {
      return this.tabs[this.currentIndex] || null;
    },
  },

  watch: {
    currentIndex: {
      immediate: true,
      handler(index) {
        this.furthestVisitedIndex = Math.max(this.furthestVisitedIndex, index);
      },
    },
  },

  methods: {
    canNavigateTo(index) {
      return this.allowAllTabs || index <= this.furthestVisitedIndex;
    },
  },
};
</script>

<style scoped>
.gs-checkout-steps {
  display: flex;
  height: 100%;
  min-width: 0;
  min-height: 0;
  padding: 24px 18px;
  background: #fff;
  border-right: 1px solid #e3e8f1;
  flex-direction: column;
}

.gs-checkout-steps__intro {
  padding: 0 7px 18px;
  border-bottom: 1px solid #edf0f5;
}

.gs-checkout-steps__intro p,
.gs-checkout-steps__actions p {
  margin: 0;
  color: #8b93a8;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-checkout-steps__intro h3 {
  margin: 6px 0 5px;
  color: var(--gs-foreground, #182230);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-checkout-steps__intro span {
  display: block;
  color: #747d95;
  font-size: 12px;
  line-height: 1.45;
}

.gs-checkout-steps__list {
  display: grid;
  flex: 1 1 auto;
  gap: 4px;
  margin: 16px 0;
  min-height: 0;
  overflow-y: auto;
  padding: 0;
  padding-right: 4px;
  list-style: none;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.gs-checkout-steps__list button {
  display: grid;
  align-items: start;
  width: 100%;
  padding: 9px 8px;
  color: #6f7890;
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 9px;
  transition: color .15s ease, background .15s ease, border-color .15s ease;
}

.gs-checkout-steps__list button:disabled {
  cursor: default;
  opacity: .62;
}

.gs-checkout-steps__list li.is-active button {
  color: #245f9f;
  background: var(--gs-active-soft, #edf7ff);
  border-color: #c9e0fa;
}

.gs-checkout-steps__list li.is-complete button:hover,
.gs-checkout-steps__list li.is-complete button:focus-visible {
  color: #245f9f;
  background: #f5faff;
  border-color: #dcecff;
  outline: none;
}

.gs-checkout-steps__number {
  display: grid;
  width: 22px;
  height: 22px;
  color: #8b93a8;
  font-size: 11px;
  font-weight: 800;
  background: #f3f5f9;
  border: 1px solid #e2e6ef;
  border-radius: 50%;
  place-items: center;
}

.is-active .gs-checkout-steps__number {
  color: #fff;
  background: var(--gs-active, #4c91f7);
  border-color: var(--gs-active, #4c91f7);
}

.is-complete .gs-checkout-steps__number {
  color: #2877c8;
  background: #e8f3ff;
  border-color: #bcd9f8;
}

.has-error .gs-checkout-steps__number {
  color: #fff;
  background: var(--gs-danger, #d94c63);
  border-color: var(--gs-danger, #d94c63);
}

.gs-checkout-steps__number svg {
  width: 13px;
  height: 13px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.gs-checkout-steps__label {
  min-width: 0;
}

.gs-checkout-steps__label strong,
.gs-checkout-steps__label small {
  display: block;
}

.gs-checkout-steps__label strong {
  color: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.gs-checkout-steps__label small {
  margin-top: 2px;
  overflow: hidden;
  color: #9299ac;
  font-size: 10px;
  line-height: 1.35;
  text-overflow: ellipsis;
}

.gs-checkout-steps__actions {
  flex: 0 0 auto;
  margin-top: auto;
  padding: 16px 7px 0;
  border-top: 1px solid #edf0f5;
}

.gs-checkout-steps__actions > div {
  display: grid;
  gap: 8px;
  margin-top: 10px;
  grid-template-columns: auto minmax(0, 1fr);
}

.gs-checkout-steps__action {
  width: 100%;
  min-width: 0;
  white-space: nowrap;
}

.gs-checkout-steps__action--primary:only-child {
  grid-column: 1 / -1;
}

@media (max-width: 900px) {
  .gs-checkout-steps {
    padding: 14px;
    border-right: 0;
    border-bottom: 1px solid #e3e8f1;
  }

  .gs-checkout-steps__intro span,
  .gs-checkout-steps__label small {
    display: none;
  }

  .gs-checkout-steps__list {
    overflow-x: auto;
    grid-auto-columns: minmax(128px, 1fr);
    grid-auto-flow: column;
  }

  .gs-checkout-steps__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 12px;
  }

  .gs-checkout-steps__actions > div {
    margin-top: 0;
    grid-template-columns: auto auto;
  }
}
</style>
